import { useState } from "react";
import { Check, X } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { ContentActionsMenu, EditedLabel } from "./ContentActionsMenu";

// One reply/comment row with ⋮ (Edit/Delete for managers, Report for others) and inline editing.
export const ThreadItem = ({ item, idKey, reportType, testIdPrefix, onSave, onDelete, variant = "reply", formatDate }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.content);
  const [saving, setSaving] = useState(false);
  const id = item[idKey];
  const initial = item.is_anonymous ? "?" : (item.author_name?.[0] || "U");
  const compact = variant === "comment";

  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try { await onSave(item, draft.trim()); setEditing(false); } finally { setSaving(false); }
  };

  return (
    <div className={`flex items-start ${compact ? "gap-2" : "gap-4"}`} data-testid={`${testIdPrefix}-${id}`}>
      <div className={`${compact ? "w-7 h-7 text-xs bg-[#E5DFEC] text-[#6B4E8A]" : "w-10 h-10 bg-[#6B4E8A] text-white"} rounded-full flex items-center justify-center font-medium shrink-0`}>
        {initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 ${compact ? "" : "mb-2"}`}>
          <span className={`${compact ? "text-xs" : ""} font-medium text-[#6B4E8A]`} data-testid={`${testIdPrefix}-${id}-author`}>{item.author_name}</span>
          {formatDate && <span className="text-xs text-[#64748B]">{formatDate(item.created_at)}</span>}
          <EditedLabel editedAt={item.edited_at} testId={`${testIdPrefix}-${id}-edited`} />
        </div>
        {editing ? (
          <div className="mt-1 space-y-2">
            <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="bg-white border-[#E5DFEC] text-sm min-h-[70px]" data-testid={`${testIdPrefix}-${id}-edit-input`} />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => { setEditing(false); setDraft(item.content); }} data-testid={`${testIdPrefix}-${id}-edit-cancel`}>
                <X className="w-3.5 h-3.5 mr-1" />Cancel
              </Button>
              <Button size="sm" className="rounded-full h-8 bg-[#6B4E8A] text-white hover:bg-[#553D6F]" disabled={saving || !draft.trim()} onClick={save} data-testid={`${testIdPrefix}-${id}-edit-save`}>
                <Check className="w-3.5 h-3.5 mr-1" />{saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          <p className={`${compact ? "text-sm" : ""} text-[#475569] whitespace-pre-wrap break-words`}>{item.content}</p>
        )}
      </div>
      <ContentActionsMenu
        testIdPrefix={`${testIdPrefix}-${id}`}
        canManage={item.can_manage}
        reportType={reportType}
        reportId={id}
        revealType={item.is_anonymous ? (reportType === "social_comment" ? "social_comment" : "discussion_reply") : undefined}
        revealId={id}
        onEdit={() => { setDraft(item.content); setEditing(true); }}
        onDelete={() => onDelete(item)}
        deleteTitle={compact ? "Delete this comment?" : "Delete this reply?"}
        deleteDescription="This permanently removes it. This cannot be undone."
      />
    </div>
  );
};

export default ThreadItem;
